/**
 * IT Helpdesk page UDA scope — the dropdown above every helpdesk screen.
 *
 * A ticket belongs to one authority (`hlpd_tckt_dtl_t.uda_id`) and every UDA shares the
 * ds_dtcp schema, so the helpdesk is scoped by a WHERE clause, not by the connection.
 * Until now that id came from the JWT alone, which meant a system admin could only read
 * another authority's helpdesk by re-issuing the whole session's tenant in the global
 * header. The pick now lives here and rides on every /helpdesk request as
 * `x-helpdesk-uda`, which resolveHelpdeskUdaId honours.
 *
 * ONE pick covers the module: the dashboard, the ticket list, the setup masters and the
 * reports are all reading the same store, and the header goes out on all their requests.
 *
 * It NARROWS ONLY. A user whose token carries a UDA keeps seeing their own authority
 * whatever the dropdown says — the server enforces that, and the control renders as a
 * read-only label for them so it never looks like a choice they do not have.
 *
 * "All UDAs" (null) is the state-level rollup. A ticket still needs one authority — one
 * with none is invisible in every UDA list and cannot be routed — so the New Ticket form
 * asks for the UDA itself rather than requiring this dropdown to be on one.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import helpdeskService from '../services/helpdeskService';

/**
 * localStorage key for the persisted pick. The axios interceptor reads this key
 * directly rather than importing this store (importing it would close a cycle through
 * the auth context) — keep the two in step. See services/interceptor.ts.
 */
export const HELPDESK_UDA_STORAGE_KEY = 'helpdesk-uda-scope';

export interface HelpdeskUdaOption {
  uda_id: number;
  uda_cd?: string;
  uda_nm: string;
}

interface HelpdeskUdaScopeState {
  udaId: number | null;
  setUdaId: (udaId: number | null) => void;
}

export const useHelpdeskUdaScopeStore = create<HelpdeskUdaScopeState>()(
  persist(
    (set) => ({
      udaId: null,
      setUdaId: (udaId) => set({ udaId }),
    }),
    { name: HELPDESK_UDA_STORAGE_KEY }
  )
);

/** Session cache for the UDA master — a fixed list several screens ask for at once. */
let udaCache: HelpdeskUdaOption[] | null = null;
let udaPromise: Promise<HelpdeskUdaOption[]> | null = null;

async function fetchUdas(): Promise<HelpdeskUdaOption[]> {
  if (udaCache) return udaCache;
  if (!udaPromise) {
    udaPromise = (async () => {
      const res = await helpdeskService.getUdas();
      const list = (res?.data || [])
        .filter((u: any) => u?.uda_id != null)
        .map((u: any) => ({
          uda_id: Number(u.uda_id),
          uda_cd: u.uda_cd,
          uda_nm: u.uda_nm || u.uda_cd || String(u.uda_id),
        }))
        .sort((a: HelpdeskUdaOption, b: HelpdeskUdaOption) => a.uda_nm.localeCompare(b.uda_nm));
      udaCache = list;
      return list;
    })().catch((err) => { udaPromise = null; throw err; });
  }
  return udaPromise;
}

export interface HelpdeskUdaScope {
  /** True when the acting user may re-scope — their JWT carries no UDA of its own. */
  canChoose: boolean;
  /** The scope in force: a uda_id, or null for the all-UDAs rollup. */
  udaId: number | null;
  /** True while no single UDA is in force — raising a ticket is refused in that state. */
  isAllUdas: boolean;
  udas: HelpdeskUdaOption[];
  loading: boolean;
  label: string;
  setUdaId: (udaId: number | null) => void;
}

export function useHelpdeskUdaScope(): HelpdeskUdaScope {
  const { user: authUser } = useAuth();
  const ownUdaId = (authUser as any)?.udaId ?? null;
  const canChoose = (authUser as any)?.isStateRegionalUser === true || ownUdaId === null;

  const picked = useHelpdeskUdaScopeStore((s) => s.udaId);
  const setUdaId = useHelpdeskUdaScopeStore((s) => s.setUdaId);

  const [udas, setUdas] = useState<HelpdeskUdaOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchUdas()
      .then((list) => { if (!cancelled) setUdas(list); })
      .catch((error) => { if (!cancelled) { console.error('Error fetching UDAs:', error); setUdas([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // A pinned user cannot re-scope: the server takes their UDA from the token whatever
  // the dropdown says, so report that one rather than a pick they cannot act on.
  const udaId = canChoose ? picked : (ownUdaId != null ? Number(ownUdaId) : null);

  const label = useMemo(() => {
    if (udaId == null) return 'All UDAs';
    return udas.find((u) => u.uda_id === udaId)?.uda_nm || `UDA #${udaId}`;
  }, [udaId, udas]);

  return { canChoose, udaId, isAllUdas: udaId == null, udas, loading, label, setUdaId };
}

interface HelpdeskUdaSelectProps {
  /** Compact suits a page header strip; false gives a full-width form field. */
  compact?: boolean;
  /** 'dark' for a coloured header, 'light' for a white surface. */
  tone?: 'light' | 'dark';
  className?: string;
}

/** The dropdown itself. A read-only label for a user the server pins to one UDA. */
export function HelpdeskUdaSelect({ compact = true, tone = 'light', className }: HelpdeskUdaSelectProps) {
  const { canChoose, udaId, udas, loading, label, setUdaId } = useHelpdeskUdaScope();
  const onDark = tone === 'dark';

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setUdaId(e.target.value === '' ? null : Number(e.target.value));
    },
    [setUdaId]
  );

  if (!canChoose) {
    return (
      <span
        className={className ?? (onDark
          ? 'inline-flex items-center gap-1 rounded bg-white/15 px-2 py-0.5 text-[11px] font-medium text-white'
          : 'inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700')}
        title="Your UDA"
      >
        <i className={onDark ? 'ri-building-2-line text-white/70' : 'ri-building-2-line text-slate-400'}></i>
        <span className="truncate max-w-[160px]">{label}</span>
      </span>
    );
  }

  return (
    <div className={className ?? 'flex items-center gap-1.5'}>
      <span className={`text-[11px] font-medium uppercase tracking-wide ${onDark ? 'text-white/60' : 'text-slate-400'}`}>UDA</span>
      <div className="relative">
        <select
          value={udaId ?? ''}
          disabled={loading}
          onChange={handleChange}
          title="Scope every helpdesk screen to one UDA"
          className={`appearance-none truncate rounded pl-2 pr-6 font-medium outline-none transition-colors disabled:cursor-wait ${
            onDark
              ? 'border border-white/25 bg-white/15 text-white hover:bg-white/25 focus:bg-white/25'
              : 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300 focus:border-blue-400'
          } ${compact ? 'w-[170px] py-0.5 text-[11px]' : 'w-full py-1.5 text-sm'}`}
        >
          <option value="" className="text-slate-800">{loading ? 'Loading UDAs...' : 'All UDAs'}</option>
          {udas.map((u) => (
            <option key={u.uda_id} value={u.uda_id} className="text-slate-800">{u.uda_nm}</option>
          ))}
        </select>
        <i className={`ri-arrow-down-s-line pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-xs ${onDark ? 'text-white/70' : 'text-slate-400'}`}></i>
      </div>
    </div>
  );
}

/**
 * Screens that render the UDA control in their OWN header — the dashboard puts it beside
 * the notification bell — and therefore suppress the strip. Two copies of one control on
 * the same page read as two different scopes, even when they share a store.
 */
const PAGES_WITH_INLINE_UDA = [
  '/ds-helpdesk/dashboard',
  '/ds-helpdesk/support-tickets',
  '/ds-helpdesk/tickets',
  // Setup and the Reports list draw their own slim masthead with the picker in it.
  '/ds-helpdesk/setup',
  '/ds-helpdesk/reports',
];

/**
 * The strip carrying the dropdown, mounted once as a pathless layout route so it sits
 * above every helpdesk screen — tickets, setup masters, reports — instead of each page
 * hosting its own copy. Pages in PAGES_WITH_INLINE_UDA host it themselves and get the
 * outlet alone.
 */
export function HelpdeskUdaScopeBar() {
  const { udaId } = useHelpdeskUdaScope();
  const { pathname } = useLocation();
  const cleanPath = pathname.replace(/\/+$/, '');
  // A single report (/reports/:id) also carries its picker in its masthead — the one the
  // report data obeys; x-helpdesk-uda never reaches the report endpoints.
  const inlineOnPage = PAGES_WITH_INLINE_UDA.some((p) => cleanPath.endsWith(p))
    || cleanPath.includes('/ds-helpdesk/reports/');

  return (
    <>
      {!inlineOnPage && (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-200 bg-white/80 px-3 py-1.5">
        <HelpdeskUdaSelect />
      </div>
      )}
      {/*
        Keyed on the scope so changing UDA REMOUNTS the page below. The pick travels as a
        request header, so nothing on the page depends on it in React's eyes: a dashboard
        that fetched in useEffect([]) would keep the UDA it loaded with and the dropdown
        would look broken. Same device Layout uses when the session tenant changes.
      */}
      <Outlet key={udaId ?? 'all-udas'} />
    </>
  );
}

export default HelpdeskUdaScopeBar;
